import type {SketchSolverIslandSnapshot} from '../../../history/solver/SketchSolverRecordTypes';
import type {
    SketchParametricOperationIntent,
    SketchParametricTemporaryDragConstraint,
} from '../../../../types/sketch/solver/SketchParametricOperationIntent';
import type {
    SketchParametricDragSessionOptions,
    SketchParametricDragSessionSolveInput,
} from '../../../../types/sketch/solver/SketchParametricDragSession';
import type {SketchParametricSolveCoordinatorResult} from '../../../../types/sketch/solver/SketchParametricSolveCoordinator';
import {SKETCH_PARAMETRIC_TOLERANCE} from '../core/SketchParametricToleranceConfig';

/**
 * FreeCAD 式草图参数拖拽会话。
 *
 * 职责：
 * 1. 在 pointerdown 时保留一份不可变 solver island，对标 GCS `initSolution()/reference`；
 * 2. 每个 pointermove 都从同一基线求解，禁止把上一帧数值漂移当成新 seed；
 * 3. 从基线实体构造低优先级临时拖拽约束，不修改关系领域或历史状态；
 * 4. `dispose()` 主动解除基线与求解端口引用，避免拖拽结束后保留整张草图对象图。
 */
export class SketchParametricDragSession {
    /** pointerdown 求解基线；释放后置空。 */
    private baselineIsland: SketchSolverIslandSnapshot | null;

    /** 单帧求解端口；释放后置空。 */
    private solveFrame: ((input: SketchParametricDragSessionSolveInput) => SketchParametricSolveCoordinatorResult) | null;

    /** 当前草图定义版本读取端口；释放后置空。 */
    private getDefinitionRevision: (() => number | null) | null;

    /** 本会话下一帧允许覆盖的草图定义版本。 */
    private expectedDefinitionRevision: number | null;

    /** 最后一次成功写回帧完成后的草图定义版本。 */
    private lastAppliedDefinitionRevision: number | null = null;

    /** 外部版本推进或运行时草图替换后永久置位，禁止旧基线再次写回。 */
    private stale = false;

    /**
     * 直线共享端点所属拓扑分量。
     *
     * 该索引只在 pointerdown 基线创建时计算一次，拖拽热路径只做 Map 查询；
     * `dispose()` 会主动清空，避免拖拽结束后继续保留点 ID 图。
     */
    private readonly lineComponentIdByPointId = new Map<string, string>();

    /**
     * @param options 拖拽基线与单帧求解端口。
     */
    public constructor(options: SketchParametricDragSessionOptions) {
        this.baselineIsland = options.baselineIsland;
        this.solveFrame = options.solve;
        this.initializeLineTopologyComponents(options.baselineIsland);
        this.getDefinitionRevision = options.getDefinitionRevision;
        this.expectedDefinitionRevision = options.getDefinitionRevision();
        this.stale = this.expectedDefinitionRevision === null;
    }

    /**
     * 从 pointerdown 基线求解并写回当前拖拽帧。
     *
     * @param operationIntents 当前帧鼠标目标。
     * @returns 写回结果；会话已释放时返回明确失败。
     */
    public preview(
        operationIntents: readonly SketchParametricOperationIntent[],
    ): SketchParametricSolveCoordinatorResult {
        if (!this.baselineIsland || !this.solveFrame || !this.getDefinitionRevision) {
            this.lastAppliedDefinitionRevision = null;
            return {
                applied: false,
                beforeIsland: null,
                solveResult: null,
                rollback: () => undefined,
                message: '草图参数拖拽会话已释放。',
            };
        }

        if (this.isStale()) {
            this.lastAppliedDefinitionRevision = null;
            return {
                applied: false,
                beforeIsland: this.baselineIsland,
                solveResult: null,
                rollback: () => undefined,
                message: '草图定义已在当前拖拽会话之外变化，已拒绝旧基线写回。',
            };
        }

        if (this.wouldDegenerateBaselineLine(this.baselineIsland, operationIntents)) {
            this.lastAppliedDefinitionRevision = null;
            return {
                applied: false,
                beforeIsland: this.baselineIsland,
                solveResult: null,
                rollback: () => undefined,
                message: '当前拖拽目标会使草图直线退化为零长，已原子拒绝本帧。',
            };
        }

        const intentsWithTemporaryConstraints = this.attachTemporaryConstraints(
            this.baselineIsland,
            operationIntents,
        );

        const result = this.solveFrame({
            baselineIsland: this.baselineIsland,
            operationIntents: intentsWithTemporaryConstraints,
        });

        if (!result.applied) {
            this.lastAppliedDefinitionRevision = null;
            return result;
        }

        const appliedDefinitionRevision = this.getDefinitionRevision();

        if (appliedDefinitionRevision === null) {
            this.stale = true;
        }
        this.expectedDefinitionRevision = appliedDefinitionRevision;
        this.lastAppliedDefinitionRevision = appliedDefinitionRevision;

        return result;
    }

    /**
     * 判断 mouseup 能否复用最后一次成功写回的预演帧。
     *
     * 拖拽策略只在“当前最终请求已经执行”时调用本方法；会话同时要求最后一帧成功，
     * 且草图定义版本自写回后未被外部操作推进。失败、过期、草图替换或版本变化都必须
     * 在 mouseup 重新求解最终目标，不能把旧帧当成本次提交结果。
     *
     * @returns 最后成功帧仍对应当前草图定义时返回 true。
     */
    public canReuseLastAppliedFrame(): boolean {
        if (this.isStale()
            || !this.getDefinitionRevision
            || this.expectedDefinitionRevision === null
            || this.lastAppliedDefinitionRevision === null) {
            return false;
        }
        const currentDefinitionRevision = this.getDefinitionRevision();

        return currentDefinitionRevision !== null
            && currentDefinitionRevision === this.expectedDefinitionRevision
            && currentDefinitionRevision === this.lastAppliedDefinitionRevision;
    }

    /**
     * 判断 pointerdown 基线是否已被外部定义变化永久淘汰。
     *
     * @returns 外部版本推进、草图删除或运行时实例替换后返回 true。
     */
    public isStale(): boolean {
        if (this.stale || !this.getDefinitionRevision || this.expectedDefinitionRevision === null) {
            return this.stale;
        }
        const currentDefinitionRevision = this.getDefinitionRevision();

        if (currentDefinitionRevision === null
            || currentDefinitionRevision !== this.expectedDefinitionRevision) {
            this.stale = true;
            this.lastAppliedDefinitionRevision = null;
        }

        return this.stale;
    }

    /**
     * 释放拖拽会话持有的草图基线与回调引用。
     */
    public dispose(): void {
        this.baselineIsland = null;
        this.solveFrame = null;
        this.lineComponentIdByPointId.clear();
        this.getDefinitionRevision = null;
        this.expectedDefinitionRevision = null;
        this.lastAppliedDefinitionRevision = null;
    }

    /**
     * 判断会话是否已释放。
     *
     * @returns 已释放时返回 true。
     */
    public isDisposed(): boolean {
        return this.baselineIsland === null
            || this.solveFrame === null
            || this.getDefinitionRevision === null;
    }

    /**
     * 判断直接鼠标目标是否会把基线中的任一直线压成零长。
     *
     * 直线方向是对称、平行、垂直等公式的定义域。若继续把精确目标
     * 交给偏好求解，严格候选会在退化点失败并回落到“最近可行”折中帧，
     * 这会污染同一手势的后续分支。会话层在求解前拒绝该单帧，下一非零帧
     * 仍从 pointerdown 不可变基线求解，因而能够穿越而不丢失端点身份。
     *
     * @param baselineIsland pointerdown 时的不可变 island。
     * @param operationIntents 当前帧直接操作目标。
     * @returns 任一受影响直线的目标长度进入退化容差时返回 true。
     */
    private wouldDegenerateBaselineLine(
        baselineIsland: SketchSolverIslandSnapshot,
        operationIntents: readonly SketchParametricOperationIntent[],
    ): boolean {
        const directTargets = new Map<string, readonly [number, number]>();

        operationIntents.forEach((intent) => {
            // 属性面板的半径/长度目标不携带点坐标，不能参与拖拽退化线段预判。
            if (intent.kind !== 'point-target' && intent.kind !== 'point-targets') {
                return;
            }
            const targets = intent.kind === 'point-target'
                ? [{pointId: intent.pointId, localPosition: intent.localPosition}]
                : intent.targets;

            targets.forEach((target) => directTargets.set(target.pointId, target.localPosition));
        });

        if (directTargets.size === 0) {
            return false;
        }

        const baselinePointPositions = new Map(
            baselineIsland.pointStates.map((pointState) => [
                pointState.pointId,
                pointState.localPosition,
            ] as const),
        );

        return baselineIsland.edgeStates.some((edgeState) => {
            if (String(edgeState.entityKind).toLowerCase() !== 'line') {
                return false;
            }
            const endpointIds = this.resolveLineEndpointIds(edgeState);
            if (!endpointIds) {
                return false;
            }
            const [startPointId, endPointId] = endpointIds;

            if (!directTargets.has(startPointId) && !directTargets.has(endPointId)) {
                return false;
            }
            const start = directTargets.get(startPointId) ?? baselinePointPositions.get(startPointId);
            const end = directTargets.get(endPointId) ?? baselinePointPositions.get(endPointId);

            return Boolean(start && end && Math.hypot(end[0] - start[0], end[1] - start[1])
                <= SKETCH_PARAMETRIC_TOLERANCE.degenerateLength);
        });
    }

    /**
     * 为直线构造基线形状和位置临时约束。
     *
     * 直接目标点所属直线允许改变长度，但优先保持另一端点的 pointerdown 位置；
     * 与被拖点拓扑独立的其余直线同时保持 pointerdown 长度与中点，在平行等关系驱动下只改变方向；
     * 与被拖点共享端点连通的变形链不添加保长/中点偏好，由正式关系与基线稳定偏好决定终态。
     *
     * @param baselineIsland pointerdown 基线。
     * @param operationIntents 当前帧操作意图。
     * @returns 已附加临时约束的操作意图。
     */
    private attachTemporaryConstraints(
        baselineIsland: SketchSolverIslandSnapshot,
        operationIntents: readonly SketchParametricOperationIntent[],
    ): readonly SketchParametricOperationIntent[] {
        if (operationIntents.length === 0) {
            return operationIntents;
        }

        const directPointIds = new Set<string>();
        operationIntents.forEach((intent) => {
            if (intent.kind === 'entity-radius-target' || intent.kind === 'entity-line-length-target') {
                return;
            }

            if (intent.kind === 'point-target') {
                directPointIds.add(intent.pointId);
                return;
            }
            intent.targets.forEach((target) => directPointIds.add(target.pointId));
        });

        const pointPositions = new Map(
            baselineIsland.pointStates.map((pointState) => [
                pointState.pointId,
                pointState.localPosition,
            ] as const),
        );
        const temporaryConstraints: SketchParametricTemporaryDragConstraint[] = [];
        const directLineComponentIds = new Set<string>();

        directPointIds.forEach((pointId) => {
            const componentId = this.lineComponentIdByPointId.get(pointId);
            if (componentId) {
                directLineComponentIds.add(componentId);
            }
        });

        baselineIsland.edgeStates.forEach((edgeState) => {
            if (String(edgeState.entityKind).toLowerCase() !== 'line') {
                return;
            }
            const endpointIds = this.resolveLineEndpointIds(edgeState);
            if (!endpointIds) {
                return;
            }
            const [startPointId, endPointId] = endpointIds;
            const start = pointPositions.get(startPointId);
            const end = pointPositions.get(endPointId);

            if (!start || !end) {
                return;
            }

            const directEndpointIds = [startPointId, endPointId].filter((pointId) => (
                pointId !== undefined && directPointIds.has(pointId)
            ));

            if (directEndpointIds.length === 1) {
                const stablePointId = directEndpointIds[0] === startPointId ? endPointId : startPointId;
                const stablePosition = stablePointId ? pointPositions.get(stablePointId) : undefined;

                if (stablePointId && stablePosition) {
                    temporaryConstraints.push({
                        kind: 'point-position',
                        constraintId: `drag-line-opposite-endpoint:${edgeState.entityId}`,
                        pointId: stablePointId,
                        targetPosition: stablePosition,
                    });
                }
                return;
            }
            if (directEndpointIds.length > 1) {
                // 整条线的两端点已有显式目标，不再添加重复形状约束。
                return;
            }

            const lineComponentId = this.lineComponentIdByPointId.get(startPointId);
            if (lineComponentId && directLineComponentIds.has(lineComponentId)) {
                /**
                 * 与被拖点共享端点连通的线属于同一变形链，而不是“未参与拖动的被动线”。
                 * 矩形右上角拖动时，底边和左边必须随正式水平/竖直关系改变长度；若继续保持
                 * 它们的长度与中点，偏好层会通过移动左下角来折中互相冲突的残差。
                 * 此处只跳过同拓扑分量，拓扑独立、仅由平行关系连接的直线仍走后续保长/中点逻辑。
                 */
                return;
            }

            const targetLength = Math.hypot(end[0] - start[0], end[1] - start[1]);
            if (!Number.isFinite(targetLength) || targetLength <= 0) {
                return;
            }

            temporaryConstraints.push({
                kind: 'line-length',
                constraintId: `drag-line-length:${edgeState.entityId}`,
                entityId: edgeState.entityId,
                targetLength,
            });
            temporaryConstraints.push({
                kind: 'line-midpoint',
                constraintId: `drag-line-midpoint:${edgeState.entityId}`,
                entityId: edgeState.entityId,
                targetPosition: [
                    (start[0] + end[0]) / 2,
                    (start[1] + end[1]) / 2,
                ],
            });
        });

        if (temporaryConstraints.length === 0) {
            return operationIntents;
        }

        const [firstIntent, ...remainingIntents] = operationIntents;
        return [{
            ...firstIntent!,
            temporaryConstraints: [
                ...(firstIntent!.temporaryConstraints ?? []),
                ...temporaryConstraints,
            ],
        }, ...remainingIntents];
    }

    /**
     * 在拖拽基线上建立直线共享端点拓扑分量索引。
     *
     * 只有共享同一个运行时点 ID 才属于同一拓扑分量；平行、垂直等几何关系只建立求解关联，
     * 不会把两条物理独立的直线误合并。这样矩形闭合边链会形成一个分量，而两条独立平行线
     * 仍分别属于不同分量。
     *
     * @param baselineIsland pointerdown 时捕获的不可变求解基线。
     */
    private initializeLineTopologyComponents(baselineIsland: SketchSolverIslandSnapshot): void {
        const adjacentPointIds = new Map<string, Set<string>>();

        baselineIsland.edgeStates.forEach((edgeState) => {
            if (String(edgeState.entityKind).toLowerCase() !== 'line') {
                return;
            }
            const endpointIds = this.resolveLineEndpointIds(edgeState);
            if (!endpointIds) {
                return;
            }
            const [startPointId, endPointId] = endpointIds;
            const startAdjacentIds = adjacentPointIds.get(startPointId) ?? new Set<string>();
            const endAdjacentIds = adjacentPointIds.get(endPointId) ?? new Set<string>();

            startAdjacentIds.add(endPointId);
            endAdjacentIds.add(startPointId);
            adjacentPointIds.set(startPointId, startAdjacentIds);
            adjacentPointIds.set(endPointId, endAdjacentIds);
        });

        const visitedPointIds = new Set<string>();
        adjacentPointIds.forEach((_adjacentIds, rootPointId) => {
            if (visitedPointIds.has(rootPointId)) {
                return;
            }

            // 分量 ID 只用于当前会话内判等，直接复用首个遍历点 ID，禁止另行生成随机标识。
            const componentId = rootPointId;
            const pendingPointIds = [rootPointId];

            while (pendingPointIds.length > 0) {
                const currentPointId = pendingPointIds.pop();
                if (!currentPointId || visitedPointIds.has(currentPointId)) {
                    continue;
                }

                visitedPointIds.add(currentPointId);
                this.lineComponentIdByPointId.set(currentPointId, componentId);
                adjacentPointIds.get(currentPointId)?.forEach((adjacentPointId) => {
                    if (!visitedPointIds.has(adjacentPointId)) {
                        pendingPointIds.push(adjacentPointId);
                    }
                });
            }
        });
    }

    /**
     * 从求解边快照解析直线起终点 ID。
     *
     * 优先读取明确的几何角色字段；旧快照缺少角色字段时回退到点 ID 顺序。
     * 非法或退化端点返回 null，调用方不得为其生成临时约束或拓扑边。
     *
     * @param edgeState 求解器边状态。
     * @returns 直线起终点 ID；无法解析时返回 null。
     */
    private resolveLineEndpointIds(
        edgeState: SketchSolverIslandSnapshot['edgeStates'][number],
    ): readonly [string, string] | null {
        const startPointId = typeof edgeState.geometry.startPointId === 'string'
            ? edgeState.geometry.startPointId
            : edgeState.pointIds[0];
        const endPointId = typeof edgeState.geometry.endPointId === 'string'
            ? edgeState.geometry.endPointId
            : edgeState.pointIds[1];

        if (!startPointId || !endPointId || startPointId === endPointId) {
            return null;
        }
        return [startPointId, endPointId];
    }
}
